/**
 * Todo/alarm over the live E2E relay (v0.6.0) — pure, testable helpers.
 *
 * AppState uses these so live mode validates daemon responses exactly like the
 * loopback `/todos` and `/alarms` clients: a malformed response becomes a real
 * error (never a partial/fake row), the daemon's fixed message + code surface
 * verbatim, and a delete only succeeds after an explicit acknowledgement.
 */
import type { AlarmManagementOp, TodoManagementOp } from '@luminis/protocol';

import {
  alarmCreateBody,
  alarmFromUnknown,
  alarmPatchBody,
  alarmsFromUnknown,
  type AlarmDraft,
  type AlarmPatch,
} from '../alarm';
import {
  todoCreateBody,
  todoFromUnknown,
  todoPatchBody,
  todosFromUnknown,
  type TodoDraft,
  type TodoPatch,
} from '../todo';
import type { Alarm, Todo } from '../types';
import { isRecord } from '../validation';

export interface TodoAlarmLiveResult {
  ok: boolean;
  data?: unknown;
  error?: { code: string; message: string };
}

/** Todo/alarm request fields (mirrors `TodoRequestPayload`/`AlarmRequestPayload`). */
export interface TodoAlarmLiveRequest {
  id?: string;
  body?: Record<string, unknown>;
}

/** Injected transport: `LiveRelayController.requestTodo` in production. */
export type TodoLiveRequester = (
  op: TodoManagementOp,
  request?: TodoAlarmLiveRequest,
) => Promise<TodoAlarmLiveResult>;

/** Injected transport: `LiveRelayController.requestAlarm` in production. */
export type AlarmLiveRequester = (
  op: AlarmManagementOp,
  request?: TodoAlarmLiveRequest,
) => Promise<TodoAlarmLiveResult>;

/** Fired acknowledgement: the advanced row, or `null` when only acked. */
export interface AlarmFiredLiveResult {
  ok: true;
  alarm: Alarm | null;
}

/** Fixed-message error text for a failed daemon response (code is appended). */
export function todoAlarmLiveRequestError(result: TodoAlarmLiveResult, fallback: string): Error {
  const message = result.error?.message ?? fallback;
  return new Error(
    result.error?.code === undefined ? message : `${message}（${result.error.code}）`,
  );
}

async function requireOkData(
  work: () => Promise<TodoAlarmLiveResult>,
  fallback: string,
): Promise<unknown> {
  const result = await work();
  if (!result.ok) throw todoAlarmLiveRequestError(result, fallback);
  return result.data;
}

function parseTodoList(data: unknown): Todo[] {
  const todos = todosFromUnknown(data);
  if (todos === null) throw new Error('daemon /todos 返回了意外数据');
  return todos;
}

function parseAlarmList(data: unknown): Alarm[] {
  const alarms = alarmsFromUnknown(data);
  if (alarms === null) throw new Error('daemon /alarms 返回了意外数据');
  return alarms;
}

/** Extract the mutated todo row from `{ todo }` (contract) or a full list. */
function todoMutationFromData(data: unknown, id?: string): Todo | null {
  if (isRecord(data)) {
    const direct = todoFromUnknown(data['todo']);
    if (direct !== null) return direct;
    const list = todosFromUnknown(data);
    if (list !== null) {
      if (id !== undefined) return list.find((todo) => todo.id === id) ?? null;
      return list.length === 1 ? (list[0] ?? null) : null;
    }
  }
  return todoFromUnknown(data);
}

function alarmMutationFromData(data: unknown, id?: string): Alarm | null {
  if (isRecord(data)) {
    const direct = alarmFromUnknown(data['alarm']);
    if (direct !== null) return direct;
    const list = alarmsFromUnknown(data);
    if (list !== null) {
      if (id !== undefined) return list.find((alarm) => alarm.id === id) ?? null;
      return list.length === 1 ? (list[0] ?? null) : null;
    }
  }
  return alarmFromUnknown(data);
}

/** DELETE only succeeds after an explicit `{ ok: true, … }` acknowledgement. */
function requireDeleteAck(data: unknown, label: string): void {
  if (!isRecord(data) || data['ok'] !== true) {
    throw new Error(`daemon ${label} 未确认删除`);
  }
}

// ---------------------------------------------------------------------------
// Todos
// ---------------------------------------------------------------------------

export async function requestTodoListLive(request: TodoLiveRequester): Promise<Todo[]> {
  return parseTodoList(await requireOkData(() => request('list'), '待办列表请求失败'));
}

export async function requestTodoCreateLive(
  request: TodoLiveRequester,
  draft: TodoDraft,
): Promise<Todo> {
  const todo = todoMutationFromData(
    await requireOkData(
      () => request('create', { body: todoCreateBody(draft) }),
      '创建待办请求失败',
    ),
  );
  if (todo === null) throw new Error('daemon 创建待办后返回了意外数据');
  return todo;
}

export async function requestTodoUpdateLive(
  request: TodoLiveRequester,
  id: string,
  patch: TodoPatch,
): Promise<Todo> {
  const todo = todoMutationFromData(
    await requireOkData(
      () => request('update', { id, body: todoPatchBody(patch) }),
      '更新待办请求失败',
    ),
    id,
  );
  if (todo === null) throw new Error('daemon 更新待办后返回了意外数据');
  return todo;
}

/** Returns the daemon's remaining list when included, else `null`. */
export async function requestTodoDeleteLive(
  request: TodoLiveRequester,
  id: string,
): Promise<Todo[] | null> {
  const data = await requireOkData(() => request('delete', { id }), '删除待办请求失败');
  requireDeleteAck(data, 'DELETE /todos');
  return todosFromUnknown(data);
}

export async function requestTodoToggleLive(request: TodoLiveRequester, id: string): Promise<Todo> {
  const todo = todoMutationFromData(
    await requireOkData(() => request('toggle', { id }), '切换待办状态请求失败'),
    id,
  );
  if (todo === null) throw new Error('daemon 切换待办状态后返回了意外数据');
  return todo;
}

// ---------------------------------------------------------------------------
// Alarms
// ---------------------------------------------------------------------------

export async function requestAlarmListLive(request: AlarmLiveRequester): Promise<Alarm[]> {
  return parseAlarmList(await requireOkData(() => request('list'), '闹钟列表请求失败'));
}

export async function requestAlarmCreateLive(
  request: AlarmLiveRequester,
  draft: AlarmDraft,
): Promise<Alarm> {
  const alarm = alarmMutationFromData(
    await requireOkData(
      () => request('create', { body: alarmCreateBody(draft) }),
      '创建闹钟请求失败',
    ),
  );
  if (alarm === null) throw new Error('daemon 创建闹钟后返回了意外数据');
  return alarm;
}

export async function requestAlarmUpdateLive(
  request: AlarmLiveRequester,
  id: string,
  patch: AlarmPatch,
): Promise<Alarm> {
  const alarm = alarmMutationFromData(
    await requireOkData(
      () => request('update', { id, body: alarmPatchBody(patch) }),
      '更新闹钟请求失败',
    ),
    id,
  );
  if (alarm === null) throw new Error('daemon 更新闹钟后返回了意外数据');
  return alarm;
}

/** Returns the daemon's remaining list when included, else `null`. */
export async function requestAlarmDeleteLive(
  request: AlarmLiveRequester,
  id: string,
): Promise<Alarm[] | null> {
  const data = await requireOkData(() => request('delete', { id }), '删除闹钟请求失败');
  requireDeleteAck(data, 'DELETE /alarms');
  return alarmsFromUnknown(data);
}

export async function requestAlarmFiredLive(
  request: AlarmLiveRequester,
  id: string,
): Promise<AlarmFiredLiveResult> {
  const data = await requireOkData(() => request('fired', { id }), '闹钟触发确认失败');
  const alarm = alarmMutationFromData(data, id);
  if (alarm !== null) return { ok: true, alarm };
  // A malformed row must not be mistaken for a bare acknowledgement.
  if (isRecord(data) && data['alarm'] !== undefined && data['alarm'] !== null) {
    throw new Error('daemon /alarms/:id/fired 返回了意外数据');
  }
  if (isRecord(data) && data['ok'] === true) return { ok: true, alarm: null };
  throw new Error('daemon /alarms/:id/fired 返回了意外数据');
}
